import { useRef } from "react";
import { isNil } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import CanvasIcon from "./CanvasIcon";
import { BADGE_STATES, CHECKLIST_ROWS } from "./recommendation-guide";
import { CHECK_CODES, CHECK_LEVELS } from "./step-checks";
import { ICON_PATHS } from "./step-look";

const LEVEL_RANK = { [CHECK_LEVELS.ERROR]: 0, [CHECK_LEVELS.WARNING]: 1 };

const RECOMMENDATION_CODES = [
  CHECK_CODES.RECOMMENDATION_WITHOUT_NEEDS,
  CHECK_CODES.NEEDS_CATEGORY_MISSING,
];

const ChecksPanel = ({
  steps,
  checksById,
  ends,
  recommendation,
  onOpen,
  onOpenRecommendation,
  onClose,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const VERIFY = t("CANVAS_EDITOR.VERIFY", { returnObjects: true });
  const RECO = VERIFY.RECOMMENDATION;
  const UNNAMED = t("CANVAS_EDITOR.STEPS.UNNAMED");
  const recommendationRef = useRef(null);
  const showRecommendation = () =>
    recommendationRef.current &&
    recommendationRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

  const items = steps
    .flatMap((step) =>
      (checksById[step.id] || []).map((item, index) => ({
        key: `${step.id}-${item.code}-${index}`,
        step,
        item,
      })),
    )
    .sort(
      (first, second) =>
        LEVEL_RANK[first.item.level] - LEVEL_RANK[second.item.level],
    );
  const errors = items.filter(
    ({ item }) => item.level === CHECK_LEVELS.ERROR,
  ).length;
  const warnings = items.length - errors;
  const nameOf = (step) => step.data.name || UNNAMED;
  const stepNames = (list) =>
    list.map((step) => step.name || UNNAMED).join(", ");

  const categoryText = (row) => {
    if (row.ok)
      return t("CANVAS_EDITOR.VERIFY.RECOMMENDATION.ASKED", {
        steps: stepNames(row.steps),
      });
    return isNil(row.candidate)
      ? RECO.NOT_ASKED
      : t("CANVAS_EDITOR.VERIFY.RECOMMENDATION.NOT_LINKED", {
          step: stepNames([row.candidate]),
        });
  };

  const rowContent = (row) => {
    if (row.kind === CHECKLIST_ROWS.CATEGORY)
      return {
        title: row.field.name,
        text: categoryText(row),
        action: RECO.GO,
      };
    if (row.kind === CHECKLIST_ROWS.BADGE)
      return {
        title: RECO.BADGE,
        text: t(`CANVAS_EDITOR.VERIFY.RECOMMENDATION.BADGE_${row.state}`, {
          step: row.step ? stepNames([row.step]) : "",
          text: row.badgeText,
        }),
        action: row.state === BADGE_STATES.SHOWN ? RECO.GO : RECO.ADD_BADGE,
      };
    return {
      title: RECO.VALUES,
      text: row.ok
        ? t("CANVAS_EDITOR.VERIFY.RECOMMENDATION.VALUES_OK", {
            scale: row.scale,
          })
        : t("CANVAS_EDITOR.VERIFY.RECOMMENDATION.VALUES_OFF", {
            steps: stepNames(row.off.map((item) => item.step)),
            scale: row.scale,
          }),
      action: RECO.GO,
    };
  };

  return (
    <div className="canvas-checks" role="dialog" aria-label={VERIFY.TITLE}>
      <div className="canvas-checks__header">
        <div>
          <h2 className="canvas-checks__title">{VERIFY.TITLE}</h2>
          <p className="canvas-checks__summary">
            {items.length
              ? t("CANVAS_EDITOR.VERIFY.SUMMARY", { errors, warnings })
              : VERIFY.NONE}
          </p>
        </div>
        <button
          type="button"
          className="canvas-checks__close"
          aria-label={VERIFY.CLOSE}
          onClick={onClose}
        >
          <CanvasIcon path={ICON_PATHS.close} strokeWidth={2} />
        </button>
      </div>
      <div className="canvas-checks__list">
        {items.length === 0 ? (
          <div className="canvas-checks__item canvas-checks__item--ok">
            <CanvasIcon path={ICON_PATHS.check} strokeWidth={2.2} />
            <span>{VERIFY.ALL_GOOD}</span>
          </div>
        ) : null}
        {items.map(({ key, step, item }) => (
          <div
            key={key}
            className={`canvas-checks__item canvas-checks__item--${item.level.toLowerCase()}`}
          >
            <CanvasIcon path={ICON_PATHS.warn} strokeWidth={1.9} />
            <div className="canvas-checks__text">
              <strong>{nameOf(step)}</strong>
              <span>{t(`CANVAS_EDITOR.CHECKS.${item.code}`, item.params)}</span>
              {RECOMMENDATION_CODES.includes(item.code) &&
              !isNil(recommendation) ? (
                <button
                  type="button"
                  className="canvas-checks__go"
                  onClick={showRecommendation}
                >
                  {`${RECO.SEE} →`}
                </button>
              ) : (
                <button
                  type="button"
                  className="canvas-checks__go"
                  onClick={() => onOpen(step.id, item.code)}
                >
                  {`${VERIFY.OPEN} →`}
                </button>
              )}
            </div>
          </div>
        ))}
        {isNil(recommendation) ? null : (
          <section
            ref={recommendationRef}
            className="canvas-checks__section"
            aria-label={RECO.TITLE}
          >
            <h3 className="canvas-checks__section-title">{RECO.TITLE}</h3>
            <p className="canvas-checks__section-hint">{RECO.HINT}</p>
            <ul className="canvas-checks__reco">
              {recommendation.map((row) => {
                const content = rowContent(row);
                return (
                  <li
                    key={row.key || row.kind}
                    className={`canvas-checks__reco-row canvas-checks__reco-row--${row.ok ? "ok" : "todo"}`}
                  >
                    <CanvasIcon
                      path={row.ok ? ICON_PATHS.check : ICON_PATHS.warn}
                      size={16}
                      strokeWidth={2.2}
                    />
                    <div className="canvas-checks__reco-text">
                      <strong>{content.title}</strong>
                      <span>{content.text}</span>
                      {row.kind === CHECKLIST_ROWS.BADGE &&
                      !row.ok &&
                      row.step ? (
                        <code>{RECO.BADGE_CODE}</code>
                      ) : null}
                    </div>
                    {isNil(row.target) ? null : (
                      <button
                        type="button"
                        className="canvas-checks__go"
                        onClick={() => onOpenRecommendation(row.target)}
                      >
                        {content.action}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        {ends.length ? (
          <div className="canvas-checks__item canvas-checks__item--info">
            <CanvasIcon path={ICON_PATHS.info} strokeWidth={1.9} />
            <div className="canvas-checks__text">
              <strong>{VERIFY.ENDS_TITLE}</strong>
              <span>
                {t("CANVAS_EDITOR.VERIFY.ENDS", {
                  steps: ends.map(nameOf).join(", "),
                })}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

ChecksPanel.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.object),
  checksById: PropTypes.object,
  ends: PropTypes.arrayOf(PropTypes.object),
  recommendation: PropTypes.arrayOf(PropTypes.object),
  onOpen: PropTypes.func.isRequired,
  onOpenRecommendation: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

ChecksPanel.defaultProps = {
  steps: [],
  checksById: {},
  ends: [],
  recommendation: null,
  onOpenRecommendation: () => undefined,
};

export default ChecksPanel;
